import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { history } from '../../../helpers';
import { userActions } from '../../../actions';
import { showToast } from '../../../services/toast';

const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{10,30}$/;
const PASSWORD_HELP =
    'Password must be 10–30 characters and include uppercase, lowercase, a number, and a special character.';

const ResetPassword = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const branding = useSelector((state) => state.organization?.branding);
    const bgStyle = branding?.cmsLoginBgUrl
        ? { backgroundImage: `url(${branding.cmsLoginBgUrl})` }
        : undefined;

    const [password, setPassword] = useState('');
    const [retypePassword, setRetypePassword] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isSubmittingRef = useRef(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [user, setUser] = useState('');
    const [code, setCode] = useState('');

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const userParam = params.get('user');
        const codeParam = params.get('code');

        if (userParam && codeParam) {
            setUser(userParam);
            setCode(codeParam);
        } else {
            setErrorMessage('Invalid reset password link.');
        }
    }, [location]);

    const handlePasswordChange = (e) => {
        setPassword(e.target.value);
        if (errorMessage) setErrorMessage('');
    };

    const handleRetypePasswordChange = (e) => {
        setRetypePassword(e.target.value);
        if (errorMessage) setErrorMessage('');
    };

    const validatePassword = () => {
        if (!password || !retypePassword) {
            setErrorMessage('Please fill in both password fields.');
            return false;
        }
        if (!PASSWORD_REGEX.test(password)) {
            setErrorMessage(PASSWORD_HELP);
            return false;
        }
        if (password !== retypePassword) {
            setErrorMessage('Passwords do not match.');
            return false;
        }
        setErrorMessage('');
        return true;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (isSubmittingRef.current) {
            return;
        }
        setSubmitted(true);

        if (!validatePassword()) {
            return;
        }
        if (!user || !code) {
            setErrorMessage('User or reset code is missing.');
            return;
        }

        isSubmittingRef.current = true;
        setIsSubmitting(true);
        const payload = {
            UserName: user,
            PasswordResetCode: code,
            NewPassword: password,
        };
        dispatch(userActions.updatePassword(payload))
            .then(() => {
                const successText = 'Password updated successfully! Please log in to your account.';
                showToast(successText, 'success');
                setErrorMessage('');
                setSuccessMessage(successText);
                setTimeout(() => {
                    history.push('/');
                }, 5000);
            })
            .catch((error) => {
                isSubmittingRef.current = false;
                setIsSubmitting(false);
                const message = error?.message || String(error);
                setErrorMessage(message);
                if (!error?.alreadyShownToast) {
                    showToast(message, 'error');
                }
            });
    };

    return (
        <React.Fragment>
            <div className='login-bgimage' style={bgStyle}>
                <div className="col-sm-8 col-sm-offset-2">
                    <div className='row' style={{ marginTop: '120px' }}>
                        <div className="col-md-6" />
                        <div className="col-md-6 card bg-white">
                            <h3 style={{ textAlign: 'center' }}>Reset Password</h3>
                            <form name="form" onSubmit={handleSubmit}>
                                <div className={'form-group' + (submitted && !password ? ' has-error' : '')}>
                                    <label htmlFor="password">Password</label>
                                    <input
                                        type="password"
                                        className="form-control"
                                        name="password"
                                        value={password}
                                        onChange={handlePasswordChange}
                                        disabled={isSubmitting}
                                        autoComplete="new-password"
                                    />
                                    <div className="text-muted mt-1" style={{ fontSize: '13px' }}>{PASSWORD_HELP}</div>
                                    {submitted && !password &&
                                        <div className="text-danger mt-1">Password is required</div>
                                    }
                                </div>

                                <div className={'form-group' + (submitted && !retypePassword ? ' has-error' : '')}>
                                    <label htmlFor="retypePassword">Re-enter Password</label>
                                    <input
                                        type="password"
                                        className="form-control"
                                        name="retypePassword"
                                        value={retypePassword}
                                        onChange={handleRetypePasswordChange}
                                        disabled={isSubmitting}
                                        autoComplete="new-password"
                                    />
                                    {submitted && !retypePassword &&
                                        <div className="text-danger mt-1">Re-enter Password is required</div>
                                    }
                                </div>

                                {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
                                {successMessage && <div className="alert alert-success">{successMessage}</div>}

                                <div className="form-group">
                                    <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                                        {isSubmitting ? 'Submitting...' : 'Submit'}
                                    </button>
                                    <Link to='/login' className='pull-right mt-10 mr-10'>Login</Link>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default ResetPassword;